/**
 * The Obean Pass on the app's pages (ADR 0022): which of a room's missions only the Pass unlocks,
 * Stripe's return from checkout, and when the next free room comes. The prices and plans live on
 * the website's /pass page, where the Pass is bought.
 */

export interface CatalogMission {
  id: string;
  name: string;
  pack?: string | undefined;
}

/** The names of the missions only the Pass unlocks that the room can't play yet, in order. */
export function lockedPassMissions(
  missions: readonly CatalogMission[],
  playable: readonly string[],
): string[] {
  return missions
    .filter((m) => m.pack !== undefined && !playable.includes(m.id))
    .map((m) => m.name);
}

/** What the URL says about a checkout the page came back from (Stripe's success and cancel URLs). */
export function passReturn(search: string): "done" | "cancelled" | null {
  const value = new URLSearchParams(search).get("pass");
  return value === "done" || value === "cancelled" ? value : null;
}

/** The /pass page, coming back to `back` after checkout. */
export const passPage = (back: string) => `/pass?back=${encodeURIComponent(back)}`;

/**
 * When the next free room comes, in the player's own time: "at 21:14", or "tomorrow at 09:30".
 * The allowance is a rolling 24 hours, so it's never further off than tomorrow.
 */
export function nextFreeLabel(at: number, now: number, locale?: string): string {
  const time = new Date(at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return new Date(at).toDateString() === new Date(now).toDateString()
    ? `at ${time}`
    : `tomorrow at ${time}`;
}

/**
 * Waits until `owns()` says yes (the webhook can land a little after Stripe sends the buyer back),
 * asking every `everyMs` for up to `forMs`. True once owned.
 */
export async function untilOwned(
  owns: () => Promise<boolean>,
  o: { everyMs?: number; forMs?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<boolean> {
  const everyMs = o.everyMs ?? 1_500;
  const forMs = o.forMs ?? 30_000;
  const sleep = o.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let waited = 0; ; waited += everyMs) {
    try {
      if (await owns()) return true;
    } catch {
      // A failed check isn't an answer: ask again.
    }
    if (waited >= forMs) return false;
    await sleep(everyMs);
  }
}
